/**
 * Game Mockup Page
 * VOY-11: Complete gaming system mockup with pool-based competition
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, TrendingUp } from 'lucide-react';
import { PoolCard } from '@/components/gaming-mockup/pool-card';
import { GameLobby } from '@/components/gaming-mockup/game-lobby';
import { GameLive } from '@/components/gaming-mockup/game-live';
import { LeaderboardFull } from '@/components/gaming-mockup/leaderboard-full';
import { DailyStreak } from '@/components/gaming-mockup/daily-streak';
import { FriendLeaderboard } from '@/components/gaming-mockup/friend-leaderboard';
import { LiveEventPool } from '@/components/gaming-mockup/live-event-pool';
import { ReferralCard } from '@/components/gaming-mockup/referral-card';
import { MysteryBox } from '@/components/gaming-mockup/mystery-box';
import { AchievementsPanel } from '@/components/gaming-mockup/achievements-panel';
import { MiniGames } from '@/components/gaming-mockup/mini-games';
import { GameAnalytics } from '@/components/gaming-mockup/game-analytics';
import {
  getGamePools,
  getGameModes,
  getQuestions,
  getAvailableBoosters,
  getLeaderboard,
  getTopPlayers,
  getStreakData,
  getFriendLeaderboard,
  getLiveEventPool,
  getReferralData,
  generateMysteryReward,
  getAchievements,
  getMiniGames,
  getGameAnalytics,
  GamePool
} from '@/data/gaming-mockup';

type GameState = 'home' | 'pool-selection' | 'lobby' | 'playing' | 'results';

export default function GameMockupPage() {
  const [gameState, setGameState] = useState<GameState>('home');
  const [selectedPool, setSelectedPool] = useState<GamePool | null>(null);
  const [finalScore, setFinalScore] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [showMysteryBox, setShowMysteryBox] = useState(false);
  const [mysteryBoxClaimed, setMysteryBoxClaimed] = useState(false);

  const pools = getGamePools();
  const gameModes = getGameModes();
  const questions = getQuestions();
  const boosters = getAvailableBoosters();
  const leaderboard = selectedPool ? getLeaderboard() : [];
  const topPlayers = getTopPlayers(10);
  
  // VOY-133: Engagement features data
  const streakData = getStreakData();
  const friendLeaderboard = getFriendLeaderboard();
  const liveEvent = getLiveEventPool();
  const referralData = getReferralData();
  const achievements = getAchievements();
  const miniGames = getMiniGames();
  const analytics = getGameAnalytics();

  const handleJoinPool = (pool: GamePool) => {
    setSelectedPool(pool);
    setGameState('lobby');
  };

  const handleStartGame = () => {
    setGameState('playing');
  };

  const handleGameComplete = (score: number) => {
    setFinalScore(score);
    setAttemptsLeft(attemptsLeft - 1);
    setMysteryBoxClaimed(false); // Reset mystery box for new game
    setGameState('results');
  };

  const handlePlayAgain = () => {
    if (attemptsLeft > 0) {
      setGameState('lobby');
    } else {
      alert('No attempts remaining!\n\nIn real app, you would need to wait or buy more attempts.');
    }
  };

  const handleBuyBoosters = () => {
    alert('Demo: Buy Boosters\n\nIn real app, this would open the booster shop.');
  };

  const handleBack = () => {
    if (gameState === 'pool-selection') {
      setGameState('home');
    } else if (gameState === 'lobby') {
      setGameState('pool-selection');
      setSelectedPool(null);
    } else if (gameState === 'results' && attemptsLeft > 0) {
      // Only allow back to lobby if attempts remaining
      setGameState('lobby');
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Hero Header */}
      {(gameState === 'home' || gameState === 'pool-selection') && (
        <div className="bg-gradient-to-r from-[#5B6FED]/90 to-[#8B9FED]/90 dark:from-[#5B6FED]/70 dark:to-[#8B9FED]/70 text-white py-12 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Compete & Win Travel</h1>
                <p className="text-white/90 text-lg">
                  Join a pool, play skill-based games, win amazing trips
                </p>
                <div className="flex gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">✈️</span>
                    <span>Win Real Trips</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🧠</span>
                    <span>Skill-Based</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">💰</span>
                    <span>$1 Entry</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4 pb-8">
        {/* Back Button (except on home, disabled on results if no attempts) */}
        {gameState !== 'home' && (
          <Button
            onClick={handleBack}
            variant="ghost"
            className="mb-4 hover:bg-white/50"
            disabled={gameState === 'results' && attemptsLeft === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        {/* Home - Engagement Features */}
        {gameState === 'home' && (
          <div className="space-y-6">
            {/* Daily Streak */}
            <DailyStreak {...streakData} />

            {/* Live Event Pool (Featured) */}
            <LiveEventPool
              {...liveEvent}
              onReserveSpot={() => alert('Demo: Reserve spot\n\nWould join the flash pool!')}
              onSetReminder={() => alert('Demo: Reminder set\n\nYou\'ll be notified 5 min before!')}
            />

            {/* Friend Leaderboard */}
            <FriendLeaderboard
              friends={friendLeaderboard}
              onChallengeFriend={(username) => alert(`Demo: Challenge ${username}\n\nWould send challenge notification!`)}
              onShareScore={() => alert('Demo: Share score\n\nWould open social share dialog!')}
              onInviteFriends={() => alert('Demo: Invite friends\n\nWould show referral options!')}
            />

            {/* Referral Card */}
            <ReferralCard
              {...referralData}
              onCopyLink={() => {
                navigator.clipboard.writeText(`https://voy.earth/?ref=${referralData.referralCode}`);
                alert('Referral link copied!');
              }}
              onShare={() => alert('Demo: Share referral\n\nWould open social share!')}
            />

            {/* Mini Games */}
            <MiniGames
              games={miniGames}
              onPlayGame={(gameId) => alert(`Demo: Playing ${gameId}\n\nWould start mini-game!`)}
            />

            {/* Achievements */}
            <AchievementsPanel
              achievements={achievements}
              totalUnlocked={achievements.filter(a => a.unlocked).length}
              totalAvailable={achievements.length}
              onShare={() => alert('Demo: Share achievements\n\nWould post to social media!')}
            />

            {/* CTA to Pools */}
            <Button
              onClick={() => setGameState('pool-selection')}
              className="w-full bg-gradient-to-r from-[#5B6FED] to-[#8B9FED] hover:opacity-90 text-white font-bold text-xl py-8 rounded-2xl shadow-xl"
            >
              <TrendingUp className="w-6 h-6 mr-2" />
              Browse Competition Pools
            </Button>
          </div>
        )}

        {/* Pool Selection */}
        {gameState === 'pool-selection' && (
          <div className="space-y-4 mt-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Competition Pool</h2>
              <p className="text-gray-600">Select a pool tier based on the trip you want to win</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {pools.map((pool) => (
                <PoolCard
                  key={pool.id}
                  pool={pool}
                  onJoin={() => handleJoinPool(pool)}
                />
              ))}
            </div>

            {/* Info Banner */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">How It Works</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>✅ Pick a pool tier (Bronze/Silver/Gold)</li>
                <li>✅ Play skill-based games (Trivia, Memory, Puzzles)</li>
                <li>✅ Get 3 attempts to improve your score</li>
                <li>✅ Top players win travel prizes</li>
                <li>✅ Prizes are real trips, not cash</li>
              </ul>
            </div>
          </div>
        )}

        {/* Game Lobby */}
        {gameState === 'lobby' && selectedPool && (
          <GameLobby
            pool={selectedPool}
            availableBoosters={boosters}
            gameModes={gameModes}
            topPlayers={topPlayers}
            yourBestScore={87340}
            yourBestRank={247}
            attemptsLeft={attemptsLeft}
            onStartGame={handleStartGame}
          />
        )}

        {/* Live Game */}
        {gameState === 'playing' && (
          <GameLive
            questions={questions}
            boosters={boosters}
            onComplete={handleGameComplete}
          />
        )}

        {/* Results & Leaderboard */}
        {gameState === 'results' && selectedPool && (
          <div className="space-y-6">
            {/* Mystery Box (Post-Game Reward) */}
            {!mysteryBoxClaimed && !showMysteryBox && (
              <Button
                onClick={() => setShowMysteryBox(true)}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-6 rounded-2xl"
              >
                🎁 Claim Your Bonus Spin!
              </Button>
            )}

            {showMysteryBox && !mysteryBoxClaimed && (
              <MysteryBox
                onSpin={generateMysteryReward}
                onClaim={(reward) => {
                  alert(`Demo: Claimed ${reward.description}!\n\nWould add to your account.`);
                  setMysteryBoxClaimed(true);
                  setShowMysteryBox(false);
                }}
              />
            )}

            {/* Game Analytics */}
            <GameAnalytics
              {...analytics}
              onPractice={() => alert('Demo: Practice mode\n\nWould start practice game!')}
              onStudyTips={() => alert('Demo: Study tips\n\nWould show improvement guides!')}
            />

            {/* Leaderboard */}
            <LeaderboardFull
              pool={selectedPool}
              entries={leaderboard}
              yourScore={finalScore}
              attemptsLeft={attemptsLeft}
              onPlayAgain={handlePlayAgain}
              onBuyBoosters={handleBuyBoosters}
            />
          </div>
        )}
      </div>
    </div>
  );
}

